import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    Divider,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { deleteAsset, downloadAsset, uploadAsset } from "../../assets/assetsSlice";
import { showSuccessToast, showErrorToast } from "../../app/utils/helpers";
import Langs from "../../app/lang/langs";

const ORANGE = "#FF9015";

export function MobileAssetsPannel(props) {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState(null);

    const handleDownload = (asset) => {
        dispatch(
            downloadAsset({
                tableName: asset.table_name,
                idParent: asset.id_parent,
                filename: asset.filename,
            })
        );
    };

    const handleUpload = (file) => {
        if (!file) return;
        console.log(file);
        const filename = file.name;
        const { idParent, tableName } = props;
        if (!idParent || !tableName) {
            showErrorToast("Erreur lors de l'envoi du fichier");
            return;
        }
        dispatch(uploadAsset({ file, tableName, idParent, filename }))
            .then((actionResponse) => {
                if (uploadAsset.fulfilled.match(actionResponse)) {
                    if (props.parentSetter) {
                        let assetTemp = {
                            filename: filename,
                            table_name: tableName,
                            id_parent: idParent,
                        };
                        props.parentSetter((prev) => ({
                            ...prev,
                            assets: [...(prev.assets || []), assetTemp],
                        }));
                    }
                    props.uploadCallBackFunction();
                }
            })
            .catch((error) => {
                console.error("Error handling in uploadAsset:", error);
            });
        // Reset input so the same file can be re-selected
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const confirmDelete = (asset) => {
        setAssetToDelete(asset);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!assetToDelete) {
            setDeleteDialogOpen(false);
            return;
        }
        dispatch(
            deleteAsset({
                tableName: assetToDelete.table_name,
                idParent: assetToDelete.id_parent,
                filename: assetToDelete.filename,
            })
        )
            .then((actionResponse) => {
                if (deleteAsset.fulfilled.match(actionResponse)) {
                    showSuccessToast("Fichier supprimé avec succès");
                    if (props.parentSetter) {
                        props.parentSetter((prev) => ({
                            ...prev,
                            assets: prev.assets.filter(
                                (asset) => asset.filename !== assetToDelete.filename
                            ),
                        }));
                    }
                    props.deleteCallBackFunction();
                }
            })
            .catch((error) => {
                console.error("Error handling in deleteAsset:", error);
            });
        setDeleteDialogOpen(false);
        setAssetToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAssetToDelete(null);
    };

    const isLimitReached = props.limitLength
        ? Array.isArray(props.assets) && props.assets.length >= props.limitLength
        : false;

    const assets = props.assets || [];

    return (
        <Box sx={{ width: "100%", pt: 2 }}>
            {/* Section title */}
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    mb: 1.5,
                    px: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    pt: 2,
                }}
            >
                <Langs str={props.label} />
            </Typography>

            {/* Asset list */}
            <Box
                sx={{
                    maxHeight: 340,
                    overflowY: "auto",
                    borderRadius: 2,
                    bgcolor: "var(--surface-card, #fff)",
                    border: "1px solid",
                    borderColor: "divider",
                    mb: 2,
                }}
            >
                {assets.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 5,
                            color: "text.secondary",
                        }}
                    >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                        <Typography variant="body2" sx={{ opacity: 0.6 }}>
                            Aucun fichier
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {assets.map((asset, index) => (
                            <React.Fragment key={asset.filename + "-" + index}>
                                <ListItem
                                    sx={{
                                        py: 1,
                                        px: 1.5,
                                        minHeight: 56,
                                        gap: 0.5,
                                    }}
                                    secondaryAction={
                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                            <IconButton
                                                onClick={() => handleDownload(asset)}
                                                sx={{
                                                    minWidth: 44,
                                                    minHeight: 44,
                                                    color: ORANGE,
                                                    "&:active": {
                                                        transform: "scale(0.97)",
                                                    },
                                                }}
                                                aria-label="Télécharger"
                                            >
                                                <DownloadOutlinedIcon />
                                            </IconButton>
                                            {!props.readOnly && (
                                                <IconButton
                                                    onClick={() => confirmDelete(asset)}
                                                    sx={{
                                                        minWidth: 44,
                                                        minHeight: 44,
                                                        color: "error.main",
                                                        "&:active": {
                                                            transform: "scale(0.97)",
                                                        },
                                                    }}
                                                    aria-label="Supprimer"
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    }
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <InsertDriveFileOutlinedIcon
                                            sx={{ color: "text.secondary", fontSize: 22 }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={asset.filename}
                                        primaryTypographyProps={{
                                            variant: "body2",
                                            sx: {
                                                fontSize: "0.875rem",
                                                wordBreak: "break-word",
                                                pr: props.readOnly ? 6 : 11,
                                            },
                                        }}
                                    />
                                </ListItem>
                                {index < assets.length - 1 && (
                                    <Divider component="li" />
                                )}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Box>

            {/* Upload button */}
            {!isLimitReached && !props.readOnly && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) => handleUpload(e.target.files[0])}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<CloudUploadOutlinedIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            bgcolor: ORANGE,
                            color: "#fff",
                            borderRadius: 999,
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "16px",
                            boxShadow: "none",
                            "&:hover": {
                                bgcolor: ORANGE,
                                boxShadow: 2,
                            },
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                        }}
                    >
                        Ajouter un fichier
                    </Button>
                </>
            )}

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        mx: 2,
                        minWidth: 280,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "1rem",
                        fontWeight: 600,
                        pb: 0,
                    }}
                >
                    <WarningAmberIcon sx={{ color: "error.main" }} />
                    Supprimer le fichier
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Voulez-vous supprimer ce fichier ?
                    </Typography>
                    {assetToDelete && (
                        <Typography
                            variant="body2"
                            sx={{ mt: 1, fontWeight: 600, wordBreak: "break-word" }}
                        >
                            {assetToDelete.filename}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            minHeight: 44,
                            px: 3,
                            color: "text.secondary",
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            minHeight: 44,
                            px: 3,
                            fontWeight: 600,
                            "&:active": {
                                transform: "scale(0.97)",
                            },
                        }}
                    >
                        Effacer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
